/**
 * =============================================================================
 *  THUC HIEN SUA VAN BAN - ban trinh duyet
 * =============================================================================
 *
 *  Port tu tools/text-edit-prototype/src/edit.mjs.
 *
 *  Nhan byte PDF goc, mot trang, mot TextBlock va van ban thay the. Tra ve byte
 *  PDF moi, hoac TU CHOI kem ly do doc duoc bang tieng Viet.
 *
 *  Trinh tu bat buoc, khong duoc doi:
 *      1. suy ra hinh hoc khoi
 *      2. phan giai font nguon                 (fonts/resolve.ts)
 *      3. kiem tra phu glyph      <- TU CHOI   (fonts/coverage.ts)
 *      4. kiem tra hinh hoc trang <- TU CHOI
 *      5. kiem tra khoang cach mau <- TU CHOI
 *      6. dat chu trong bbox, xuong dong khi tran (BOX-LOCAL, ADR-005)
 *      7. che glyph cu, ve glyph moi, nhung subset font
 *
 * =============================================================================
 *  GIOI HAN CO CHU DICH: DAY LA VE DE LEN, KHONG PHAI XOA
 * =============================================================================
 *  Ham nay ve mot hinh chu nhat mau nen DE LEN glyph cu roi ve chu moi ben tren.
 *
 *  GLYPH CU VAN CON NGUYEN TRONG CONTENT STREAM CUA FILE XUAT RA. Van ban goc
 *  VAN TRICH XUAT DUOC bang copy-paste, bang tim kiem, bang bat ky cong cu doc
 *  PDF nao.
 *
 *  He qua bat buoc phai noi ro:
 *    - DAY KHONG PHAI CHE THONG TIN (redaction). Tuyet doi khong duoc dung
 *      duong nay cho redact.apply, va tuyet doi khong duoc trinh bay voi nguoi
 *      dung nhu la da xoa noi dung. Xem ADR-007.
 *    - Ban Rust/PDFium bat buoc phai lam khac: XOA dung cac toan tu Tj/TJ cua
 *      khoi trong content stream roi chen toan tu moi (PDFPageObj_* cua PDFium
 *      + goi bat buoc FPDFPage_GenerateContent(), ADR-004).
 *    - Ve de len khong cap nhat StructTreeRoot, nen tai lieu tagged co the vi
 *      pham PDF/UA sau khi sua.
 *    - Hinh chu nhat che dung mau do ben goi dua vao (mac dinh trang). Tren nen
 *      mau, nen anh, hoac trong bang co to nen, ve de len SE NHIN THAY RO.
 *
 * =============================================================================
 *  BON KIEU "THANH CONG GIA" MA HAM NAY PHAI CHAN
 * =============================================================================
 *  Ca bon deu sinh ra mot PDF HOP LE ma trich xuat ra DUNG van ban moi - nen
 *  moi phep kiem dua tren trich xuat van ban deu bao "dat". Ca bon deu la che
 *  do hong THAT, do kiem chung doi nghich tim ra tren tai lieu that.
 *
 *  1. CHU VO HINH. Ve chu moi bang MAU GOC len tren hinh chu nhat che mau trang:
 *     chu trang tren nen trang. -> chan boi cua chan khoang cach mau (BUOC 5).
 *  2. PHEP KIEM "VAN NAM TRONG KHOI" TU LUA. Vi ta ve de len chu khong xoa, khoi
 *     CU van con do; phep kiem chon khoi chong lan nhieu nhat se tim thay khoi
 *     CU va so no voi chinh no - luon dat. -> ta ghi lai newFontBaseName de tang
 *     kiem chung nhan ra run MOI bang FONT DA THUC SU GHI RA.
 *  3. PHEP KIEM ".notdef" VO NGHIA. Dem U+FFFD/U+0000 trong van ban trich xuat
 *     khong chung minh gi: /ToUnicode va glyph duoc ve la hai thu doc lap, mot
 *     trang toan o vuong van trich xuat hoan hao. -> chi doi chieu ma Unicode
 *     voi CHUONG TRINH FONT (glyph id != 0) moi co gia tri (BUOC 3).
 *  4. THIEU KIEM TRA HINH HOC TRANG. /Rotate != 0 dat chu sai he toa do; bbox
 *     khoi gom nhom qua tay co the RONG HON CA TRANG va chu bi ve ra ngoai mep.
 *     -> BUOC 4.
 */
import {
  PDFDocument,
  rgb,
  pushGraphicsState,
  popGraphicsState,
  beginText,
  endText,
  showText,
  setFontAndSize,
  setCharacterSpacing,
  setWordSpacing,
  setCharacterSqueeze,
  setTextMatrix,
  setFillingRgbColor,
  PDFName,
  type PDFRef,
} from 'pdf-lib';
import type { PdfErrorCode, PdfRect, TextBlock } from '@pdfeditor/types';

import { rawFontkit } from '../fonts/fontkit';
import { checkCoverage, describeMissing } from '../fonts/coverage';
import {
  resolveFont,
  type EmbeddedFontSource,
  type ResolvedFont,
  type TextFaceStyle,
} from '../fonts/resolve';
import type { FontFamilyId } from '../fonts/families';
import { deriveBlockGeometry, type BlockGeometry } from './blockGeometry';
import { computeCoverRect } from './coverRect';
import { inferAlign, layoutInBox, type LayoutResult, type TextAlign, type TextState } from './layout';

/**
 * Khoang cach mau toi thieu (tong tri tuyet doi tren ba kenh RGB, thang 0..3)
 * giua mau chu moi va mau hinh chu nhat che nen. Duoi nguong nay chu se VO HINH.
 * 0.15 tuong duong khoang 5% do sang tren mot kenh - du de bat "trang tren
 * trang" va "den tren den" ma khong bat nham chu xam nhat tren nen trang.
 */
export const COLOR_MIN_DISTANCE = 0.15;

export type TextReplaceOutcome =
  | 'thanh-cong'
  | 'khoi-khong-co-hinh-hoc'
  | 'van-ban-khong-doi'
  | 'tu-choi-font'
  | 'tu-choi-thieu-glyph'
  | 'tu-choi-trang-xoay'
  | 'tu-choi-khoi-ngoai-trang'
  | 'tu-choi-mau-trung-nen'
  | 'tu-choi-khong-vua-khoi'
  | 'trang-ngoai-pham-vi'
  | 'khong-mo-duoc-de-ghi'
  | 'khong-nhung-duoc-font'
  /** Che chu ma van con go chu moi - hai y dinh loai tru nhau. */
  | 'che-nhung-van-co-chu';

export interface TextReplaceReport {
  ok: boolean;
  outcome: TextReplaceOutcome;
  /**
   * 'cover' = CHI ve hinh chu nhat nen, khong ve chu moi.
   *
   * Lo ra ngoai vi giao dien phai noi khac han: "che" chu khong phai "sửa", va
   * canh bao chu goc VAN NAM TRONG TEP phai duoc nhac lai ngay tai cho. Xem
   * truong `cover` cua lenh text.replace trong hop dong kieu.
   */
  mode: 'replace' | 'cover';
  /** Ma loi cua hop dong kieu, de PdfCoreBrowser dung lai nguyen ven. */
  errorCode: PdfErrorCode | null;
  /** Ly do doc duoc, tieng Viet, hien thang cho nguoi dung. */
  reason: string;

  /** Tang font da phan giai. Luon co, ke ca khi that bai o buoc sau. */
  tier: ResolvedFont['tier'];
  tierLabel: string;
  /** true = hinh thuc tai lieu DA DOI. Giao dien BAT BUOC phai canh bao. */
  substituted: boolean;
  fontReason: string;
  /**
   * HO FONT DONG GOI da THUC SU duoc dung de dat chu, la ma ho font (vd
   * "noto-serif"). null khi dung font nhung trong tep (tang A) hoac khi che chu.
   *
   * Giao dien hien no de nguoi dung biet phong nao that su duoc ghi: neu ho chon
   * Noto Serif ma cua chan lui ve Noto Sans, day la cho duy nhat noi ra su that.
   *
   * TUY CHON: baseReport() luon dat no (null khi che chu / tang A), nhung de
   * tuy chon de cac vo bao cao dung san o tang giao dien khong phai doi cung luc.
   */
  familyResolved?: string | null;
  fsType: number | null;
  fsTypeTrusted: boolean;

  /**
   * Can le DA DUOC DUNG de dat chu. Luon co gia tri khi da chay den buoc 6.
   *
   * Ban xem truoc BAT BUOC doc truong nay chu khong duoc tu suy ra can le mot
   * lan nua: do la dung cai bay ma hop che da roi vao mot lan (hai ben cung tinh
   * mot thu, roi troi nhau, va ban xem truoc noi doi trong hai vong). Mot phep
   * tinh, hai ben doc.
   */
  align: TextAlign;
  /**
   * Can le DO DUOC tu chu cu. Bang `align` khi ben goi khong chi dinh gi.
   * Giao dien hien no lam trang thai ban dau de nguoi dung thay ta nhan ra gi.
   */
  alignDetected: TextAlign;

  lineCount: number;
  shrinkRatio: number | null;
  fontSizePt: number | null;
  neededHeightPt: number | null;
  widestLinePt: number | null;
  colorDistance: number | null;
  pageRotation: number | null;

  /**
   * Ten /BaseFont ma pdf-lib da thuc su ghi ra.
   * Tang kiem chung CAN no: xem "thanh cong gia" so 2 o dau file.
   */
  newFontBaseName: string | null;

  /**
   * Hop che chu cu, toa do PDF. Chi co khi ok.
   *
   * Lo ra ngoai vi BAN XEM TRUOC phai ve DUNG hop nay. No cao hon bbox khi chu
   * cu co dau tieng Viet chong tang - xem coverRect.ts. Ban xem truoc tu tinh
   * lay hop rieng la cach chac chan de hai ben noi hai chuyen khac nhau.
   */
  coverRect: PdfRect | null;

  /** Chi co khi ok. */
  bytes: Uint8Array | null;
}

export interface TextReplaceParams {
  pageIndex: number;
  block: TextBlock;
  newText: string;
  /** Font nhung san cua khoi. null khi khong lay duoc - tang B se lo. */
  fontSource: EmbeddedFontSource | null;
  /** Mau hinh chu nhat che glyph cu. Mac dinh trang. */
  backgroundRgb?: readonly [number, number, number];
  /**
   * Can le nguoi dung chon. BO QUA = SUY RA TU CHU CU, khong phai = 'left'.
   * Xem inferAlign() trong layout.ts.
   */
  align?: TextAlign;
  /** Hop chua pham vi, CHI de suy ra can le. Xem PreflightInput.alignContainer. */
  alignContainer?: PdfRect;
  /** Kieu chu cho chu MOI. Yeu cau dam/nghieng se doi sang mat chu that. */
  face?: TextFaceStyle;
  /** HO FONT cho chu MOI. Bo qua = ho mac dinh (noto-sans). */
  family?: FontFamilyId;
  /**
   * CO CHU cho chu MOI, tinh bang diem. Bo qua = giu co chu do duoc tu chu cu.
   * Duong dat chu VAN thu nho de vua khoi (ADR-005), nen co chu lon hon cho co
   * the bi giam - bao cao noi ro qua shrinkRatio / fontSizePt.
   */
  fontSizePt?: number;
  /** true = CHE chu cu, khong ve chu moi. `newText` phai rong. */
  cover?: boolean;
}

function baseReport(): TextReplaceReport {
  return {
    ok: false,
    outcome: 'tu-choi-font',
    mode: 'replace',
    errorCode: 'unsupported-feature',
    reason: '',
    tier: 'none',
    tierLabel: 'không-phân-giải-được',
    substituted: false,
    fontReason: '',
    familyResolved: null,
    fsType: null,
    fsTypeTrusted: false,
    align: 'left',
    alignDetected: 'left',
    lineCount: 0,
    shrinkRatio: null,
    fontSizePt: null,
    neededHeightPt: null,
    widestLinePt: null,
    colorDistance: null,
    pageRotation: null,
    newFontBaseName: null,
    coverRect: null,
    bytes: null,
  };
}

/**
 * Doc /BaseFont cua font vua nhung, sau khi doc.save() da vat chat hoa no.
 * Tra ve null khi khong doc duoc - ben goi phai coi do la "khong xac dinh",
 * KHONG duoc coi la dat.
 */
function readBaseFontName(doc: PDFDocument, fontRef: PDFRef): string | null {
  try {
    const dict = doc.context.lookup(fontRef) as { get?: (k: PDFName) => unknown } | undefined;
    if (!dict || typeof dict.get !== 'function') return null;
    const bf = dict.get(PDFName.of('BaseFont')) as { asString?: () => string } | undefined;
    if (!bf) return null;
    const s = typeof bf.asString === 'function' ? bf.asString() : String(bf);
    return s.startsWith('/') ? s.slice(1) : s;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Kiem tra truoc - khong cham vao byte PDF
// ---------------------------------------------------------------------------

export interface PreflightInput {
  block: TextBlock;
  newText: string;
  fontSource: EmbeddedFontSource | null;
  /** Kich thuoc va do xoay trang, lay tu DocumentInfo. */
  page: { widthPt: number; heightPt: number; rotation: number };
  backgroundRgb?: readonly [number, number, number];
  /** Bo qua = suy ra tu chu cu. Xem TextReplaceParams.align. */
  align?: TextAlign;
  /**
   * Hop ma pham vi nam trong, do ben goi tinh (scope.alignContainerFor).
   *
   * CHI dung lam moc DO can le. Hop DAT CHU van la bbox cua pham vi: noi rong
   * hop dat chu ra ca container se cho chu tran sang vung khong kiem soat duoc.
   * Bo qua = do le trong chinh bbox, y het ban truoc.
   */
  alignContainer?: PdfRect;
  face?: TextFaceStyle;
  /** HO FONT cho chu MOI. Bo qua = ho mac dinh. Cua chan phu glyph chay tren no. */
  family?: FontFamilyId;
  /** CO CHU cho chu MOI, diem. Bo qua = giu co chu do duoc tu chu cu. */
  fontSizePt?: number;
  cover?: boolean;
}

/**
 * Du lieu de tang ghi dung lai, KHONG tinh lai. Chi co khi report.ok.
 *
 * Hai bien the vi hai duong ghi that su khac nhau: che chu khong dat chu, nen
 * no khong co bo cuc, khong co trang thai chu, khong co chuoi da tao hinh. Cho
 * chung mot kieu du lieu roi de trong mot nua se bat duong ghi phai doan xem
 * hom nay no dang o nhanh nao.
 */
export type TextReplacePlan =
  | {
      mode: 'replace';
      geometry: BlockGeometry;
      resolved: ResolvedFont;
      layout: LayoutResult;
      state: TextState;
      shapedText: string;
      backgroundRgb: readonly [number, number, number];
    }
  | {
      mode: 'cover';
      geometry: BlockGeometry;
      resolved: ResolvedFont;
      backgroundRgb: readonly [number, number, number];
    };

export interface PreflightResult {
  report: TextReplaceReport;
  plan: TextReplacePlan | null;
}

/**
 * Chay TOAN BO cac cua chan tu choi ma khong can byte PDF.
 *
 * Ton tai de apply() tu choi NGAY khi nguoi dung bam, thay vi ghi nhan mot lenh
 * roi that bai am tham luc luu - luc do nguoi dung da di lam viec khac va tin
 * rang tai lieu da duoc sua.
 *
 * Kich thuoc trang o day lay tu pdf.js (view/CropBox). Tang ghi kiem LAI bang
 * kich thuoc cua pdf-lib (MediaBox): hai nguon co the lech nhau, va cua chan
 * cuoi cung phai la cua chan cua chinh thu vien ghi ra file.
 */
export async function preflightTextReplace(input: PreflightInput): Promise<PreflightResult> {
  const report = baseReport();
  const backgroundRgb = input.backgroundRgb ?? ([1, 1, 1] as const);

  // --- BUOC 1: hinh hoc khoi ---
  const geometry = deriveBlockGeometry(input.block);
  if (!geometry) {
    report.outcome = 'khoi-khong-co-hinh-hoc';
    report.errorCode = 'unsupported-feature';
    report.reason =
      'Khối văn bản này không có đủ dữ liệu hình học để đặt chữ (không có đoạn chữ nào, ' +
      'hoặc khung bao có bề rộng/chiều cao bằng 0). Không thể sửa mà không đoán bừa vị trí.';
    return { report, plan: null };
  }

  // =========================================================================
  //  CHE CHU - mot duong rieng, va no phai duoc goi dung ten
  // =========================================================================
  //  Truoc day van ban rong bi tu choi thang: khong co chu moi thi khong co gi
  //  de ve. Nhung nguoi dung VAN can mot cach bo mot doan chu di, va khong co
  //  cach nao thi ho se go vai dau cach de "xoa" - cho ra dung mot mang che, chi
  //  la khong ai noi cho ho biet do la mot mang che.
  //
  //  Nen thao tac do duoc lam THANH THAT: ve dung hinh chu nhat che, va noi ro
  //  no la CHE chu khong phai XOA. Hai dieu bat buoc di kem:
  //    - giao dien goi no la "Che chữ", khong bao gio la "Xóa";
  //    - canh bao "chu goc van nam trong tep va van sao chep ra duoc" duoc nhac
  //      lai ngay tai cho, khong phai o mot dong chan trang nao do.
  //  Xem ADR-007: day TUYET DOI khong phai che thong tin.
  if (input.cover === true) {
    if (input.newText.length > 0) {
      report.outcome = 'che-nhung-van-co-chu';
      report.errorCode = 'unsupported-feature';
      report.reason =
        'Thao tác che chữ không nhận nội dung mới. Hãy dùng chức năng sửa chữ nếu muốn thay chữ.';
      return { report, plan: null };
    }
    return preflightCover(input, report, geometry, backgroundRgb);
  }

  if (input.newText.length === 0) {
    report.outcome = 'van-ban-khong-doi';
    report.errorCode = 'unsupported-feature';
    report.reason =
      'Văn bản mới đang để trống. Đường sửa này vẽ đè chứ không xóa, nên "xóa hết chữ" ' +
      'chỉ tạo ra một mảng che trắng mà chữ cũ vẫn nằm nguyên trong tệp. Hãy dùng nút ' +
      '"Che chữ" nếu bạn muốn phủ chữ cũ, hoặc chức năng che thông tin (redaction) của lõi ' +
      'thật nếu muốn xóa hẳn.';
    return { report, plan: null };
  }

  // --- BUOC 2: phan giai font nguon ---
  // HO FONT nguoi dung chon di vao day: tang B se nap DUNG ho do, va cua chan
  // phu glyph (buoc 3) se chay tren chinh no - khong phai tren ho mac dinh.
  const resolved = await resolveFont(input.fontSource, input.newText, input.face, input.family);
  report.tier = resolved.tier;
  report.tierLabel = resolved.tierLabel;
  report.substituted = resolved.substituted;
  report.fontReason = resolved.reason;
  report.familyResolved = resolved.fontFamily;
  report.fsType = resolved.fsType;
  report.fsTypeTrusted = resolved.fsTypeTrusted;

  if (!resolved.ok || !resolved.font || !resolved.bytes) {
    // Phan biet hai ly do tu choi RAT khac nhau ve mat san pham:
    //   thieu glyph -> van de KY THUAT
    //   con lai     -> khong phan giai duoc font
    const blockedByGlyphs = resolved.attempts.some(
      (a) => !a.ok && a.why.startsWith('thiếu-glyph'),
    );
    report.outcome = blockedByGlyphs ? 'tu-choi-thieu-glyph' : 'tu-choi-font';
    report.errorCode = 'font-not-editable';
    report.reason = resolved.reason;
    return { report, plan: null };
  }

  // --- BUOC 3: kiem phu glyph mot lan nua, tren font DA CHON ---
  // resolveFont da kiem, nhung day la cua chan cuoi truoc khi ghi. Kiem hai lan
  // re hon rat nhieu so voi mot tai lieu day o vuong trong.
  const cov = checkCoverage(resolved.font, input.newText);
  if (!cov.ok) {
    report.outcome = 'tu-choi-thieu-glyph';
    report.errorCode = 'font-not-editable';
    report.reason =
      `Font đã chọn thiếu glyph cho ${cov.missing.length} ký tự: ${describeMissing(cov.missing)}. ` +
      'Nếu vẫn vẽ, những ký tự này sẽ ra ô vuông trống trong tài liệu.';
    return { report, plan: null };
  }
  const shapedText = input.newText.normalize(cov.form ?? 'NFC');

  // --- BUOC 4: CUA CHAN HINH HOC TRANG ---
  //
  //  Hai gia dinh am tham cua toan bo duong toa do ben duoi:
  //    (1) trang khong xoay (/Rotate = 0),
  //    (2) bbox cua khoi nam trong trang.
  //  Ca hai deu SAI tren tai lieu that (3.8% trang xoay, 9.2% khoi vuot ra
  //  ngoai trang do gom nhom keo cac cot xa nhau lam mot).
  const rotation = ((input.page.rotation % 360) + 360) % 360;
  report.pageRotation = rotation;
  if (rotation !== 0) {
    report.outcome = 'tu-choi-trang-xoay';
    report.errorCode = 'unsupported-feature';
    report.reason =
      `Trang này có /Rotate = ${rotation}°. Đường đặt chữ hiện tại tính theo hệ tọa độ CHƯA xoay ` +
      'nên chữ mới sẽ nằm sai chỗ hoàn toàn khi trình xem áp phép xoay. Lõi Rust/PDFium sẽ xử lý ' +
      'phép xoay; bản web từ chối thay vì ghi ra một tệp hỏng.';
    return { report, plan: null };
  }

  const tol = 1;
  const bb = geometry.bbox;
  const outOfPage =
    bb.x < -tol ||
    bb.y < -tol ||
    bb.x + bb.width > input.page.widthPt + tol ||
    bb.y + bb.height > input.page.heightPt + tol;
  if (outOfPage) {
    report.outcome = 'tu-choi-khoi-ngoai-trang';
    report.errorCode = 'unsupported-feature';
    report.reason =
      `Khung bao của khối (${bb.x.toFixed(0)},${bb.y.toFixed(0)} ${bb.width.toFixed(0)}×${bb.height.toFixed(0)}pt) ` +
      `nằm ngoài khổ trang ${input.page.widthPt.toFixed(0)}×${input.page.heightPt.toFixed(0)}pt. ` +
      'Việc gom nhóm đã kéo các phần xa nhau vào làm một khối; sửa sẽ vẽ chữ ra ngoài mép trang.';
    return { report, plan: null };
  }

  // --- BUOC 5: CUA CHAN MAU - chan chinh sua VO HINH ---
  // Xem "thanh cong gia" so 1 o dau file.
  const textRgb = geometry.colorRgb;
  const colorDistance =
    Math.abs(textRgb[0] - backgroundRgb[0]) +
    Math.abs(textRgb[1] - backgroundRgb[1]) +
    Math.abs(textRgb[2] - backgroundRgb[2]);
  report.colorDistance = Math.round(colorDistance * 1000) / 1000;
  if (colorDistance < COLOR_MIN_DISTANCE) {
    report.outcome = 'tu-choi-mau-trung-nen';
    report.errorCode = 'unsupported-feature';
    report.reason =
      `Màu chữ gốc trùng với màu che nền (khoảng cách ${colorDistance.toFixed(3)} < ${COLOR_MIN_DISTANCE}). ` +
      'Vẽ chữ mới bằng màu này sẽ cho ra một chỉnh sửa VÔ HÌNH: tệp vẫn hợp lệ, vẫn trích xuất ' +
      'đúng chữ mới, nhưng người dùng chỉ nhìn thấy một mảng trắng. Cần xác định màu nền thật ' +
      'của vùng này trước khi sửa.';
    return { report, plan: null };
  }

  // --- BUOC 6: dat chu trong bbox (BOX-LOCAL, ADR-005) ---
  //
  // CO CHU: nguoi dung co the yeu cau mot co chu khac. Do la diem BAT DAU cua
  // viec dat chu; layoutInBox VAN thu nho tu do de vua khoi, nen mot co chu lon
  // hon o co the bi giam lai - va bao cao noi ro (shrinkRatio, fontSizePt). Buoc
  // dong duoc scale theo ty le co chu moi / co chu goc de dat chu 24pt khong dat
  // sat nhau nhu dat chu 12pt. Bo qua = giu co chu do duoc tu chu cu, y nhu truoc.
  const requestedSizePt =
    input.fontSizePt !== undefined && input.fontSizePt > 0 ? input.fontSizePt : geometry.fontSizePt;
  const lineHeightPt =
    geometry.fontSizePt > 0
      ? geometry.lineHeightPt * (requestedSizePt / geometry.fontSizePt)
      : geometry.lineHeightPt;
  const state: TextState = {
    fontSizePt: requestedSizePt,
    charSpacing: geometry.charSpacing,
    wordSpacing: geometry.wordSpacing,
    horizontalScale: geometry.horizontalScale,
  };
  // Can le: DO tu chu cu truoc, roi moi de lua chon tuong minh de len. Bao cao
  // mang ca hai so - giao dien can biet ta nhan ra gi de hien lam trang thai dau.
  const alignDetected = inferAlign(
    input.block.runs,
    bb,
    geometry.fontSizePt,
    input.alignContainer,
  );
  const align = input.align ?? alignDetected;
  report.alignDetected = alignDetected;
  report.align = align;

  const layout = layoutInBox(
    resolved.font,
    shapedText,
    {
      widthPt: bb.width,
      heightPt: bb.height,
      lineHeightPt,
      // MEP TRAI CUA HOP LA bb.x, khong phai geometry.firstLineX.
      //
      // Ban dau cho nay dung firstLineX de can le trai giu nguyen tuyet doi toa
      // do cu. Test bat duoc ngay: voi mot TIEU DE CAN GIUA thi firstLineX nam
      // sau ben trong hop (chu bat dau o giua), nen "firstLineX + bb.width" tro
      // toi mot mep phai AO nam ngoai ca kho trang - can phai se day chu ra
      // ngoai mep giay.
      //
      // Hop dat chu phai la CHINH hop bao cua pham vi thi ba cong thuc can le
      // moi quy chieu ve cung mot he. Cai gia phai tra: can le trai bay gio dat
      // chu o bb.x thay vi firstLineX. Voi chu von da can trai, hai gia tri chi
      // lech nhau phan side bearing cua chu cai dau (mot phan diem). Voi chu von
      // KHONG can trai, dat ve bb.x moi la dung nghia "căn trái".
      originXPt: bb.x,
    },
    state,
    align,
  );
  report.lineCount = layout.lines.length;
  report.shrinkRatio = layout.shrinkRatio;
  report.fontSizePt = layout.fontSizePt;
  report.neededHeightPt = layout.neededHeightPt;
  report.widestLinePt = layout.widestLinePt;

  if (!layout.ok) {
    report.outcome = 'tu-choi-khong-vua-khoi';
    report.errorCode = 'unsupported-feature';
    report.reason =
      'Văn bản mới không vừa khối kể cả khi đã thu nhỏ cỡ chữ tới hạn. ' +
      `Cần ${layout.neededHeightPt.toFixed(1)}pt chiều cao và ${layout.widestLinePt.toFixed(1)}pt bề rộng, ` +
      `khối chỉ có ${bb.height.toFixed(1)}×${bb.width.toFixed(1)}pt. ` +
      'Theo ADR-005, việc sửa chỉ được dồn chữ TRONG khối — không được đẩy các khối bên cạnh ' +
      'xuống, cũng không được tràn sang trang sau. Hãy rút ngắn văn bản.';
    return { report, plan: null };
  }

  // Chu moi co the vua bbox nhung mep phai/day van vuot khoi TRANG khi khoi
  // nam sat mep. Kiem rieng vi bbox hop le khong bao ham chu hop le.
  // Mep phai lay tu CAC DONG DA DAT chu khong phai tu firstLineX + rong nhat:
  // voi can phai / can giua, dong dai nhat khong con bat dau o firstLineX.
  const drawRight = layout.maxRightPt;
  const drawBottom =
    geometry.firstBaselineY -
    (layout.lines.length - 1) * layout.lineHeightPt -
    layout.fontSizePt * 0.25;
  if (drawRight > input.page.widthPt + tol || drawBottom < -tol) {
    report.outcome = 'tu-choi-khoi-ngoai-trang';
    report.errorCode = 'unsupported-feature';
    report.reason =
      `Chữ mới sẽ vẽ ra ngoài khổ trang (mép phải ${drawRight.toFixed(0)}pt, đáy ${drawBottom.toFixed(0)}pt ` +
      `trên trang ${input.page.widthPt.toFixed(0)}×${input.page.heightPt.toFixed(0)}pt).`;
    return { report, plan: null };
  }

  report.ok = true;
  report.outcome = 'thanh-cong';
  report.errorCode = null;
  report.reason = resolved.substituted
    ? `Sửa được, NHƯNG phải thay font: ${resolved.reason}`
    : `Sửa được bằng ${resolved.tierLabel}.`;

  // Hop che duoc tinh O DAY, mot lan, roi dung cho CA duong ghi lan ban xem
  // truoc. Truoc day duong ghi tu tinh con ban xem truoc dung thang bbox: sau
  // khi duong ghi biet no rong dau tieng Viet thi ban xem truoc van che hut, va
  // nguoi dung thay dau chu cu lo lung tren chu moi. Ban xem truoc phai la mo
  // phong cua duong ghi, khong duoc tu tinh lay mot phien ban rieng.
  const lastBaselineY =
    geometry.firstBaselineY - (geometry.lineCount - 1) * geometry.lineHeightPt;
  report.coverRect = computeCoverRect({
    bbox: geometry.bbox,
    firstBaselineY: geometry.firstBaselineY,
    lastBaselineY,
    fontSizePt: geometry.fontSizePt,
    // Chi can TAP KY TU de biet dinh muc cao toi dau, khong can thu tu doc.
    originalText: input.block.runs.map((r) => r.text).join(''),
    font: resolved.font,
  });

  return {
    report,
    plan: { mode: 'replace', geometry, resolved, layout, state, shapedText, backgroundRgb },
  };
}

/**
 * =============================================================================
 *  CHAY THU MOT LAN CHE CHU
 * =============================================================================
 *  Che chu bo qua BON trong sau cua chan cua duong sua chu, va tung cai deu co
 *  ly do chu khong phai cho de:
 *
 *    BUOC 2 (phan giai font)  - VAN CHAY, nhung de DO chu khong de ghi. Hop che
 *        phai bao het pham vi muc cua chu CU, va pham vi do do bang chuong trinh
 *        font (coverRect.ts). Khong co font thi lui ve so do du phong.
 *    BUOC 3 (phu glyph)       - bo. Khong ve ky tu nao thi khong thieu glyph nao.
 *    BUOC 5 (khoang cach mau) - bo. Cua chan do chan "chu moi vo hinh tren nen
 *        che"; o day khong co chu moi. Mot mang che cung mau nen la KET QUA MONG
 *        MUON, khong phai loi.
 *    BUOC 6 (dat chu)         - bo. Khong co chu de dat.
 *
 *  BUOC 4 (hinh hoc trang) thi VAN GIU nguyen: mot trang xoay se dat hinh chu
 *  nhat che sai cho y het nhu no dat chu sai cho, va mot mang trang ve nham vao
 *  giua doan van khac con te hon la khong che duoc.
 */
async function preflightCover(
  input: PreflightInput,
  report: TextReplaceReport,
  geometry: BlockGeometry,
  backgroundRgb: readonly [number, number, number],
): Promise<PreflightResult> {
  report.mode = 'cover';

  const bb = geometry.bbox;
  const rotation = ((input.page.rotation % 360) + 360) % 360;
  report.pageRotation = rotation;
  if (rotation !== 0) {
    report.outcome = 'tu-choi-trang-xoay';
    report.errorCode = 'unsupported-feature';
    report.reason =
      `Trang này có /Rotate = ${rotation}°. Hình chữ nhật che được tính theo hệ tọa độ CHƯA xoay ` +
      'nên nó sẽ phủ nhầm chỗ khi trình xem áp phép xoay. Bản web từ chối thay vì bôi trắng một ' +
      'vùng không liên quan.';
    return { report, plan: null };
  }

  const tol = 1;
  const outOfPage =
    bb.x < -tol ||
    bb.y < -tol ||
    bb.x + bb.width > input.page.widthPt + tol ||
    bb.y + bb.height > input.page.heightPt + tol;
  if (outOfPage) {
    report.outcome = 'tu-choi-khoi-ngoai-trang';
    report.errorCode = 'unsupported-feature';
    report.reason =
      `Khung bao của phạm vi (${bb.x.toFixed(0)},${bb.y.toFixed(0)} ` +
      `${bb.width.toFixed(0)}×${bb.height.toFixed(0)}pt) nằm ngoài khổ trang. Việc gom nhóm đã ` +
      'kéo các phần xa nhau vào làm một khối; che sẽ bôi trắng cả vùng không liên quan. ' +
      'Hãy thu hẹp phạm vi (Alt+↓) rồi che từng phần.';
    return { report, plan: null };
  }

  // Font o day la THUOC DO, khong phai vat lieu ghi. Chuoi dich la chu CU: ta
  // can font do duoc dinh dau thanh cua chinh no. Che chu KHONG ghi phong nao,
  // nen `familyResolved` cua bao cao van la null - ho font o day chi lam thuoc
  // do du phong khi khong phan giai duoc font goc.
  const originalText = input.block.runs.map((r) => r.text).join('');
  const resolved = await resolveFont(input.fontSource, originalText, undefined, input.family);
  report.tier = resolved.tier;
  report.tierLabel = resolved.tierLabel;
  // KHONG bao "da thay font": khong co chu nao duoc go lai bang font khac. Bao
  // substituted=true o day se lam giao dien hien canh bao doi font cho mot thao
  // tac khong he cham vao font nao - va mot canh bao keu sai cho la mot canh bao
  // nguoi dung se hoc cach bo qua.
  report.substituted = false;
  report.fontReason = resolved.reason;
  report.fsType = resolved.fsType;
  report.fsTypeTrusted = resolved.fsTypeTrusted;

  const lastBaselineY =
    geometry.firstBaselineY - (geometry.lineCount - 1) * geometry.lineHeightPt;
  report.coverRect = computeCoverRect({
    bbox: geometry.bbox,
    firstBaselineY: geometry.firstBaselineY,
    lastBaselineY,
    fontSizePt: geometry.fontSizePt,
    originalText,
    // Khong phan giai duoc font thi computeCoverRect() tu lui ve FALLBACK_INK_BAND,
    // la so do truong hop XAU NHAT cua tieng Viet. Che rong hon mot chut con hon
    // de lo dau thanh cua chu cu tho ra tren mang che.
    font: resolved.font,
  });

  report.ok = true;
  report.outcome = 'thanh-cong';
  report.errorCode = null;
  report.lineCount = 0;
  report.fontSizePt = geometry.fontSizePt;
  report.reason =
    'Sẽ phủ một hình chữ nhật màu nền lên phạm vi này. ĐÂY LÀ CHE, KHÔNG PHẢI XÓA: chữ gốc vẫn ' +
    'nằm nguyên trong tệp và vẫn sao chép, tìm kiếm ra được. Không dùng cách này để che thông ' +
    'tin nhạy cảm.';

  return { report, plan: { mode: 'cover', geometry, resolved, backgroundRgb } };
}

// ---------------------------------------------------------------------------
// Duong ghi
// ---------------------------------------------------------------------------

/**
 * Thuc hien mot lan sua van ban va tra ve byte PDF moi.
 *
 * @param pdfBytes byte PDF GOC. Ham nay KHONG BAO GIO ghi vao mang nay (ADR-003)
 *        - pdf-lib chi doc va dung mot tai lieu moi trong bo nho.
 */
export async function applyTextReplace(
  pdfBytes: Uint8Array,
  params: TextReplaceParams,
): Promise<TextReplaceReport> {
  // --- Mo tai lieu de lay kich thuoc trang THAT tu thu vien se ghi file ---
  let doc: PDFDocument;
  try {
    doc = await PDFDocument.load(pdfBytes, {
      ignoreEncryption: true,
      updateMetadata: false,
      throwOnInvalidObject: false,
    });
  } catch (err) {
    const report = baseReport();
    report.outcome = 'khong-mo-duoc-de-ghi';
    report.errorCode = 'parse-failed';
    report.reason =
      'Không mở được tài liệu để ghi. Tệp có thể được mã hóa bằng cách mà bản web chưa xử lý được, ' +
      `hoặc cấu trúc tệp bị hỏng. (${String(err).slice(0, 120)})`;
    return report;
  }

  const pages = doc.getPages();
  const page = pages[params.pageIndex];
  if (!page) {
    const report = baseReport();
    report.outcome = 'trang-ngoai-pham-vi';
    report.errorCode = 'page-out-of-range';
    report.reason = `Trang ${params.pageIndex + 1} nằm ngoài tài liệu (${pages.length} trang).`;
    return report;
  }

  const pageSize = page.getSize();
  const pageRotation = ((page.getRotation().angle % 360) + 360) % 360;

  // Cua chan cuoi cung chay tren so lieu cua CHINH pdf-lib, khong phai so lieu
  // pdf.js dua ra o buoc apply(). Hai nguon co the lech (CropBox vs MediaBox).
  const { report, plan } = await preflightTextReplace({
    block: params.block,
    newText: params.newText,
    fontSource: params.fontSource,
    page: { widthPt: pageSize.width, heightPt: pageSize.height, rotation: pageRotation },
    ...(params.backgroundRgb ? { backgroundRgb: params.backgroundRgb } : {}),
    ...(params.align ? { align: params.align } : {}),
    ...(params.alignContainer ? { alignContainer: params.alignContainer } : {}),
    ...(params.face ? { face: params.face } : {}),
    ...(params.family ? { family: params.family } : {}),
    ...(params.fontSizePt !== undefined ? { fontSizePt: params.fontSizePt } : {}),
    ...(params.cover ? { cover: true } : {}),
  });
  if (!plan) return report;

  // --- CHE CHU: chi mot hinh chu nhat, roi dung ---------------------------
  // Khong nhung font, khong mo khoi van ban, khong phat mot toan tu chu nao.
  // Hop che lay DUNG con so preflight da tinh - xem ghi chu o nhanh duoi.
  if (plan.mode === 'cover') {
    const box = report.coverRect;
    if (!box) {
      report.ok = false;
      report.outcome = 'khoi-khong-co-hinh-hoc';
      report.errorCode = 'unsupported-feature';
      report.reason = 'Không tính được hình chữ nhật che cho phạm vi này.';
      return report;
    }
    page.drawRectangle({
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height,
      color: rgb(plan.backgroundRgb[0], plan.backgroundRgb[1], plan.backgroundRgb[2]),
      borderWidth: 0,
    });
    report.bytes = await doc.save({ useObjectStreams: false });
    return report;
  }

  const { geometry, resolved, layout, state, backgroundRgb } = plan;

  // --- Nhung font: TAO SUBSET MOI tu font nguon da phan giai ---
  doc.registerFontkit(rawFontkit as Parameters<PDFDocument['registerFontkit']>[0]);

  let embedded;
  try {
    // subset: true -> chi nhung glyph thuc su dung. Day chinh la "tao subset moi
    // tu font nguon da phan giai" ma toan bo bo do nay dan toi.
    embedded = await doc.embedFont(resolved.bytes as Uint8Array, { subset: true });
  } catch {
    // Tao subset that bai thuong do font co bang khong chuan. Thu nhung nguyen ban.
    try {
      embedded = await doc.embedFont(resolved.bytes as Uint8Array, { subset: false });
    } catch (err2) {
      report.ok = false;
      report.outcome = 'khong-nhung-duoc-font';
      report.errorCode = 'font-not-editable';
      report.reason = `Không nhúng được font vào tài liệu: ${String(err2).slice(0, 120)}`;
      report.bytes = null;
      return report;
    }
  }

  // --- Che glyph cu ---
  // GIOI HAN CO CHU DICH: day la VE DE LEN, khong phai xoa. Glyph cu van nam
  // trong content stream va van copy-paste ra duoc. Xem khoi ghi chu dau file.
  //
  // Hop che phai bao het PHAM VI MUC chu khong phai hop chu: dau tieng Viet
  // chong hai tang vuon toi 1.048em, vuot ra ngoai hop cao 1.0em, va truoc day
  // bi bo lai lo lung tren chu moi. Xem coverRect.ts.
  // Dung DUNG hop che ma preflight da tinh. Tinh lai o day la mo duong cho hai
  // ben lech nhau - va ban xem truoc doc chinh con so nay.
  const cover =
    report.coverRect ??
    computeCoverRect({
      bbox: geometry.bbox,
      firstBaselineY: geometry.firstBaselineY,
      lastBaselineY:
        geometry.firstBaselineY - (geometry.lineCount - 1) * geometry.lineHeightPt,
      originalText: params.block.runs.map((r) => r.text).join(''),
      fontSizePt: geometry.fontSizePt,
      font: resolved.font,
    });
  page.drawRectangle({
    x: cover.x,
    y: cover.y,
    width: cover.width,
    height: cover.height,
    color: rgb(backgroundRgb[0], backgroundRgb[1], backgroundRgb[2]),
    borderWidth: 0,
  });

  // --- Ve chu moi bang TOAN TU THAT, khong dung drawText ---
  // Ly do: drawText cua pdf-lib khong phat ra Tc, Tw hay Tz. Ma ba tham so do
  // chinh la thu quyet dinh chu moi co trung khop bo cuc cu hay khong.
  const fontKey = page.node.newFontDictionary(embedded.name, embedded.ref);
  const [r, g, b] = geometry.colorRgb;

  const ops = [
    pushGraphicsState(),
    beginText(),
    setFillingRgbColor(r, g, b),
    setFontAndSize(fontKey, layout.fontSizePt),
    setCharacterSpacing(state.charSpacing),
    setWordSpacing(state.wordSpacing),
    setCharacterSqueeze(state.horizontalScale),
  ];

  // Duong co so dong dau giu nguyen vi tri cu -> chu moi ngoi dung cho chu cu.
  //
  // Toa do x lay TU layout.placedLines chu khong tinh lai o day. Can le da duoc
  // quyet dinh o buoc 6 va bao cao da mang no ra ngoai cho ban xem truoc; tinh
  // lai o day la mo lai dung cai khe cho hai ben troi nhau.
  let baseline = geometry.firstBaselineY;
  for (const line of layout.placedLines) {
    if (line.text.length > 0) {
      ops.push(setTextMatrix(1, 0, 0, 1, line.xPt, baseline));
      ops.push(showText(embedded.encodeText(line.text)));
    }
    baseline -= layout.lineHeightPt;
  }
  ops.push(endText(), popGraphicsState());
  page.pushOperators(...ops);

  const outBytes = await doc.save({ useObjectStreams: false });

  // Ten /BaseFont ma pdf-lib da thuc su ghi ra - moc duy nhat de tang kiem chung
  // nhan ra run MOI. Xem "thanh cong gia" so 2 o dau file.
  report.newFontBaseName = readBaseFontName(doc, embedded.ref);
  report.bytes = outBytes;
  return report;
}
